/**
 * House of Anuk — Cart Drawer Web Component
 * The sacred vessel slides in from the right.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._bindEvents();
  }

  _bindEvents() {
    document.querySelectorAll('[data-cart-trigger]').forEach(btn => {
      btn.addEventListener('click', () => this.open());
    });
    this.querySelectorAll('[data-cart-close]').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });
    const overlay = document.getElementById('site-overlay');
    if (overlay) overlay.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.hidden) this.close();
    });
    this._bindRemoveButtons();
  }

  _bindRemoveButtons() {
    this.querySelectorAll('[data-remove-item]').forEach(btn => {
      btn.addEventListener('click', () => this._removeItem(btn.dataset.removeItem));
    });
  }

  open() {
    this.removeAttribute('hidden');
    this.setAttribute('aria-hidden', 'false');
    document.getElementById('site-overlay')?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-cart-close]')?.focus();
  }

  close() {
    this.setAttribute('hidden', '');
    this.setAttribute('aria-hidden', 'true');
    document.getElementById('site-overlay')?.classList.remove('is-active');
    document.body.style.overflow = '';
    document.querySelector('[data-cart-trigger]')?.focus();
  }

  async _removeItem(key) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 }),
      });
      if (res.ok) {
        const cart = await res.json();
        this._updateCount(cart.item_count);
        this._refreshDrawer(cart);
      }
    } catch (err) {}
  }

  _updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count > 0 ? count : '';
    });
  }

  _money(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    });
  }

  _imgUrl(url, size) {
    if (!url) return '';
    return url.replace(/\.(jpg|jpeg|gif|png|webp)(\?|$)/i, `_${size}.$1$2`);
  }

  _esc(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  _refreshDrawer(cart) {
    const body = this.querySelector('#cart-drawer-body');
    if (!body) return;

    if (cart.item_count === 0) {
      this._showEmpty();
      return;
    }

    body.innerHTML = cart.items.map(item => {
      const imgSrc = this._imgUrl(item.featured_image?.url, '120x');
      const imgHTML = imgSrc
        ? `<img src="${imgSrc}" alt="${this._esc(item.product_title)}" loading="lazy" width="60" height="60">`
        : '';
      const variantHTML = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-item__variant ui-label">${this._esc(item.variant_title)}</p>`
        : '';

      return `<div class="cart-item" data-key="${item.key}">
        <div class="cart-item__image">${imgHTML}</div>
        <div class="cart-item__details">
          <p class="cart-item__title">${this._esc(item.product_title)}</p>
          ${variantHTML}
          <p class="cart-item__price ui-label">${this._money(item.final_line_price)}</p>
        </div>
        <button class="cart-item__remove ui-label" data-remove-item="${item.key}" aria-label="Remove ${this._esc(item.product_title)}">✕</button>
      </div>`;
    }).join('');

    let footer = this.querySelector('.cart-drawer__footer');
    if (!footer) {
      footer = document.createElement('div');
      footer.className = 'cart-drawer__footer';
      this.querySelector('.cart-drawer__inner').appendChild(footer);
    }
    footer.innerHTML = `
      <div class="cart-drawer__subtotal">
        <span class="ui-label">Subtotal</span>
        <span class="cart-drawer__subtotal-price">${this._money(cart.total_price)}</span>
      </div>
      <a href="/checkout" class="btn btn--full">Checkout</a>
    `;

    this._bindRemoveButtons();
  }

  _showEmpty() {
    const body = this.querySelector('#cart-drawer-body');
    const footer = this.querySelector('.cart-drawer__footer');
    if (body) body.innerHTML = `<p class="cart-drawer__empty mantra">${window.cartEmptyText || 'Your vessel is empty. The temple awaits.'}</p>`;
    if (footer) footer.remove();
  }
}

customElements.define('cart-drawer', CartDrawer);

/* ── Add to cart intercept ─────────────────────────────────── */
document.addEventListener('submit', async (e) => {
  const form = e.target;
  if (!form.matches('[action="/cart/add"]') && !form.id?.startsWith('product-form-')) return;

  const addBtn = form.querySelector('[name="add"]');
  if (!addBtn) return;

  e.preventDefault();

  const originalText = addBtn.textContent;
  addBtn.disabled = true;
  addBtn.textContent = 'Adding…';

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
    });

    if (res.ok) {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();

      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
      });

      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        drawer._refreshDrawer(cart);
        drawer.open();
      }
    }
  } catch (err) {
    /* Silently fail */
  } finally {
    addBtn.disabled = false;
    addBtn.textContent = originalText;
  }
});
